import { useEffect, useState } from "react";
import { Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

const initialForm = { name: "", email: "", phone: "", service: "New website", budget: "", details: "", is_nonprofit: false };

export default function BookingDialog({ open, service, onOpenChange }) {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) setForm((current) => ({ ...current, service, is_nonprofit: service === "Nonprofit Rate" }));
  }, [open, service]);

  const update = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));

  const submit = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/bookings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!response.ok) throw new Error("Booking request failed");
      toast.success("Project inquiry sent. Tybra will respond within 24 hours.");
      setForm(initialForm);
      onOpenChange(false);
    } catch (error) {
      toast.error("The inquiry could not be sent. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="booking-dialog" className="top-[calc(50%+0.75rem)] max-h-[calc(100dvh-2rem)] w-[calc(100vw-1rem)] max-w-2xl overflow-hidden border-cyan-300/25 bg-[#0B0B12] p-0 text-white shadow-[0_0_120px_rgba(0,240,255,0.18)] sm:top-[50%]">
        <div data-lenis-prevent className="max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain">
        <div className="rounded-t-lg border-b border-white/10 bg-cyan-300/[0.06] p-6 sm:p-8">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl font-extrabold uppercase tracking-[-0.04em] text-white">Book a discovery call</DialogTitle>
            <DialogDescription className="mt-3 text-sm leading-relaxed text-slate-400">Share the essentials. Tybra will reply with a recommended path within 24 hours.</DialogDescription>
          </DialogHeader>
        </div>
        <form data-testid="booking-form" onSubmit={submit} className="grid gap-5 p-6 sm:grid-cols-2 sm:p-8">
          <div className="space-y-2"><Label htmlFor="booking-name">Name</Label><Input data-testid="booking-name-input" id="booking-name" required minLength="2" value={form.name} onChange={update("name")} className="form-input" placeholder="Your name" /></div>
          <div className="space-y-2"><Label htmlFor="booking-email">Email</Label><Input data-testid="booking-email-input" id="booking-email" required type="email" value={form.email} onChange={update("email")} className="form-input" placeholder="you@company.com" /></div>
          <div className="space-y-2"><Label htmlFor="booking-phone">Phone</Label><Input data-testid="booking-phone-input" id="booking-phone" required type="tel" minLength="7" maxLength="30" value={form.phone} onChange={update("phone")} className="form-input" placeholder="479-555-0134" /></div>
          <div className="space-y-2"><Label htmlFor="booking-service">Service</Label><select data-testid="booking-service-select" id="booking-service" value={form.service} onChange={(event) => setForm((current) => ({ ...current, service: event.target.value, is_nonprofit: event.target.value === "Nonprofit Rate" }))} className="form-input h-11 w-full rounded-md px-3"><option>New website</option><option>Website refresh</option><option>Ongoing care</option><option>Nonprofit Rate</option><option>Custom mobile app</option><option>Custom feature</option></select></div>
          <div className="space-y-2 sm:col-span-2"><Label htmlFor="booking-budget">Budget signal</Label><Input data-testid="booking-budget-input" id="booking-budget" value={form.budget} onChange={update("budget")} className="form-input" placeholder="$800+" /></div>
          <label data-testid="booking-nonprofit-label" className="flex cursor-pointer items-center gap-3 rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-4 text-sm text-slate-300 transition-colors hover:border-cyan-300/35 sm:col-span-2">
            <input
              data-testid="booking-nonprofit-checkbox"
              type="checkbox"
              checked={form.is_nonprofit}
              onChange={(event) => setForm((current) => ({ ...current, is_nonprofit: event.target.checked }))}
              className="h-4 w-4 accent-cyan-300"
            />
            This inquiry is for a 501c, charity, or church
          </label>
          <div className="space-y-2 sm:col-span-2"><Label htmlFor="booking-details">Project details</Label><Textarea data-testid="booking-details-textarea" id="booking-details" required minLength="10" value={form.details} onChange={update("details")} className="form-input min-h-32" placeholder="What are you building, fixing, or launching?" /></div>
          <Button data-testid="booking-form-submit-button" disabled={loading} className="cyan-button h-12 rounded-full font-mono text-xs font-bold uppercase tracking-[0.2em] sm:col-span-2">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send project inquiry
          </Button>
        </form>
        </div>
      </DialogContent>
    </Dialog>
  );
}
